/**
 * React Query hook for the app update gate.
 *
 * Fetches and derives the update status once on startup. Fails open (the query
 * fn never rejects), retries once, and is cached for the session so the gate
 * doesn't re-run on every screen.
 */

import { useQuery } from '@tanstack/react-query';
import { checkAppVersion } from '../helpers/appVersionApi.helper';
import type { AppVersionCheckResult } from '../helpers/appVersionApi.helper';

const STALE_TIME_MS = 30 * 60 * 1000; // 30 min — version requirements change rarely
const GC_TIME_MS = 60 * 60 * 1000;

export const appVersionCheckQueryKey = ['appVersionCheck'] as const;

export const useAppVersionCheck = () =>
  useQuery<AppVersionCheckResult>({
    queryKey: appVersionCheckQueryKey,
    queryFn: checkAppVersion,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: false,
  });
